// @flow
import { get, has, isEmpty } from 'lodash';
import { constants } from '@clubmed/utils';

import { ICONS } from 'Components/utils/icon/icons';
import { COLORS } from 'Components/utils/color/colors';

import { type ProductFeatureDetailProps } from 'Components/widgets/product-feature-detail/ProductFeatureDetail.jsx';
import { type ProductFeaturesCategoryProps } from 'Components/widgets//product-features/ProductFeatures.jsx';
import { type ProductFeatureCardProps } from 'Components/widgets/product-feature-card/ProductFeatureCard.jsx';

import { featuresIcons } from './productFeatures.icons';

function getRank(item: ProductFeaturesCategoryData | ProductFeaturesActivityData): number {
  return Object.values(constants.RANKINGS).findIndex(ranking => ranking === item.ranking);
}

function sortActivities(a, b) {
  return getRank(b) - getRank(a);
}

function getHowToBookDesc({ hasBookingOnline, hasBookingOnsite, config }) {
  if (!hasBookingOnline && !hasBookingOnsite) {
    return '';
  }

  if (hasBookingOnline && hasBookingOnsite) {
    return config.common.bookOnlineOrOnsite;
  }

  return hasBookingOnline ? config.common.bookOnlineOnly : config.common.bookOnsiteOnly;
}

function mapServices(
  services = [],
  {
    included,
    onDemand,
    age,
    group,
    equipmentsLabel,
    utilitiesLabel,
    durationLabel,
    config,
    excursionPackingLabel = '',
    excursionSeasonalityLabel = '',
    excursionTransportationLabel = '',
  }: ProductFeaturesLineProps,
) {
  return services.map(
    ({
      hasBookingOnline,
      hasBookingOnsite,
      id,
      isIdealForFamily,
      description,
      detailedDescription,
      ageRestriction,
      durationDescription,
      label,
      level,
      note,
      group: groupDescription,
      specificConditions,
      equipments,
      utilities,
      seasonality,
      transportation,
      whatToBring,
      targetPrice,
    }) => {
      const items = [
        {
          description,
        },
        {
          label: config.common.treatmentsLabel,
          description: detailedDescription,
        },
        {
          label: group,
          description: groupDescription,
        },
        {
          label: age,
          description: ageRestriction,
          ...(specificConditions ? { note: specificConditions } : {}),
        },
        {
          label: excursionSeasonalityLabel,
          description: seasonality,
        },
        {
          label: excursionTransportationLabel,
          description: transportation,
        },
        {
          label: excursionPackingLabel,
          description: whatToBring,
        },
        {
          label: equipmentsLabel,
          items: (equipments || []).map(equipment => ({
            description: equipment.label,
            tag: equipment.included
              ? { color: COLORS.BLUE, label: included }
              : { color: COLORS.AMBER, label: onDemand },
          })),
        },
        {
          label: utilitiesLabel,
          items: (utilities || []).map(utility => ({
            description: utility.label,
            tag: utility.included
              ? { color: COLORS.BLUE, label: included }
              : { color: COLORS.AMBER, label: onDemand },
          })),
        },
        {
          label: config.common.howToBookLabel,
          description: getHowToBookDesc({ hasBookingOnline, hasBookingOnsite, config }),
        },
      ].filter(item => item.description || (item.items && !isEmpty(item.items)));

      const price =
        targetPrice && targetPrice.price
          ? { ...targetPrice, label: get(config, 'common.price.priceFrom', '+{price}{currency}') }
          : null;

      const badges = isIdealForFamily
        ? [{ label: config.common.idealForFamilyLabel, iconName: ICONS.IDEAL_FOR_FAMILY }]
        : null;

      return {
        ...(badges ? { badges } : {}),
        id,
        items,
        ...(level ? { level } : {}),
        ...(durationDescription
          ? {
              duration: {
                label: durationLabel,
                description: durationDescription,
              },
            }
          : {}),
        title: label,
        // $FlowFixMe
        ...(note ? { note } : {}),
        ...(price ? { targetPrice: price } : {}),
      };
    },
  );
}

function mapFacilities(facilities = [], props: ProductFeaturesLineProps, inside) {
  const {
    distance: distanceLabel,
    indoor: indoorLabel,
    nightLighting: nightLightingLabel,
    openingHours: openingHoursLabel,
    outdoor: outdoorLabel,
    typeDescription: typeDescriptionLabel,
    location: locationLabel,
  } = props;

  return facilities
    .filter(({ onSite }) => onSite === inside)
    .map(
      ({
        id,
        description,
        distance,
        label,
        nightLighting,
        openingHours,
        outdoor,
        quantity,
        specificInformation = [],
        technicalSpecificities = {},
        localisation,
      }) => {
        const basicInformation = [
          {
            label: distanceLabel,
            description: distance.toString(),
          },
          {
            description: nightLighting ? nightLightingLabel : '',
          },
          {
            description: outdoor ? outdoorLabel : indoorLabel,
          },
          {
            label: locationLabel,
            description: localisation,
          },
          {
            label: openingHoursLabel,
            description: `${get(openingHours, 'opening', '')} ${get(
              openingHours,
              'closing',
              '',
            )}`.trim(),
          },
        ];

        const mapSpecificInformation = specificInformation.map(information => ({
          label: get(props, get(information, 'type'), ''),
          description: get(information, 'value', ''),
        }));

        const mapTechnicalSpecificities = Object.keys(technicalSpecificities)
          .filter(
            key =>
              key !== 'unit' &&
              technicalSpecificities[key] &&
              !isEmpty(technicalSpecificities[key].toString()),
          )
          .map(key => {
            if (key === 'typeDescription') {
              return {
                label: typeDescriptionLabel,
                description: technicalSpecificities.typeDescription,
              };
            }

            return {
              label: get(props, key, ''),
              description: `${technicalSpecificities[key]} ${get(
                technicalSpecificities,
                'unit',
                'm',
              )}`,
            };
          });

        const items = [
          ...basicInformation,
          ...mapTechnicalSpecificities,
          ...mapSpecificInformation,
        ].filter(item => item && item.description);

        return {
          id,
          description,
          items,
          title: label,
          quantity,
        };
      },
    );
}

function mapGeneralInformation(activityId, props, activities: Array<ProductFeaturesActivityData>) {
  const activity = activities.find(({ id }) => id === activityId);

  if (!activity) return [];

  const { bookingLabel, termsAndConditionsLabel, supervisionLabel, equipmentRentalLabel } = props;

  const {
    bookingDescription,
    termsAndConditionsDescription,
    supervisionDescription,
    equipmentRentalDescription,
  } = activity;

  return [
    {
      label: bookingLabel,
      description: bookingDescription || '',
    },
    {
      label: termsAndConditionsLabel,
      description: termsAndConditionsDescription || '',
    },
    {
      label: supervisionLabel,
      description: supervisionDescription || '',
    },
    {
      label: equipmentRentalLabel,
      description: equipmentRentalDescription || '',
    },
  ].filter(info => info.description && info.description.trim());
}

function mapChildcareInformation(
  { opening, philosophy }: ProductChildcaresLineProps,
  childcare = {},
) {
  if (isEmpty(childcare)) return {};

  const items = [
    {
      label: philosophy,
      description: childcare.philosophy,
    },
  ].filter(item => has(item, 'description') || has(item, 'note'));

  return {
    duration: {
      label: opening,
      description: childcare.duration,
    },
    items,
    note: childcare.furtherInformation,
  };
}

function mapActivityData({
  activityDetail: {
    childcare = {},
    facilities = [],
    includedServices = [],
    onDemandServices = [],
    resort = {},
  },
  activityId = '',
  color = COLORS.BLUE,
  description,
  icon = ICONS.TRIDENT1,
  image,
  isIncluded,
  title,
  notIncludedMessage,
  productFeaturesLineData,
  activities,
  philosophy,
  brandLogoUrl,
}: {
  activityDetail: ProductFeatureDetailData,
  activityId: string,
  color: ?string,
  description: ?string,
  hasBanner: boolean,
  icon: ?string,
  image: string,
  isIncluded: boolean,
  title: string,
  notIncludedMessage?: string,
  productFeaturesLineData: ProductFeaturesLineProps,
  activities: Array<ProductFeaturesActivityData>,
  philosophy: ?string,
  brandLogoUrl: ?string,
}): ProductFeatureDetailProps {
  const isFacilityActivity = [
    'services_and_facilities_0_2',
    'services_and_facilities_2_3',
    'services_and_facilities_4_10',
  ].some(id => activityId.includes(id));

  const {
    config,
    facilitiesTitle,
    generalInformationTitle,
    includedTitle,
    onDemandTitle,
    outsideFacilitiesTitle,
    resortFacilitiesTitle,
  } = productFeaturesLineData;

  return {
    ...(description ? { description } : null),
    diamond: {
      colorBg: color,
      colorBorder: color,
      iconName: icon,
    },
    downloadLinksList: [
      {
        iconName: ICONS.DOWNLOAD,
        label: config.common.downloadLeafletLabel,
        // $FlowFixMe
        url: productFeaturesLineData?.resort?.wellnessActivitiesIntroduction?.bookletUrl,
      },
    ].filter(i => i.url),
    equipmentsTitle: get(config, 'common.allInclusive', ''),
    equipments: isFacilityActivity
      ? [...(resort.facilities || []), ...(resort.productActivities || [])].map(e => ({
          items: e.equipments.map(i => ({
            iconName: ICONS[i.id],
            label: i.label,
          })),
          title: e.subtitle || e.label,
        }))
      : null,
    immersive: {
      url: image,
    },
    brandInformation: isEmpty(childcare)
      ? {
          title: config.common.philosophyLabel,
          description: philosophy,
          imageSrc: brandLogoUrl,
        }
      : null,
    mainInformationTitle: get(childcare, 'label', ''),
    // $FlowFixMe
    mainInformation: mapChildcareInformation(productFeaturesLineData, childcare),
    isIncluded,
    notIncludedMessage: notIncludedMessage || '',
    includedMessage: '',
    // $FlowFixMe
    includedServices: mapServices(includedServices, productFeaturesLineData),
    includedTitle,
    // $FlowFixMe
    onDemandServices: mapServices(onDemandServices, productFeaturesLineData),
    onDemandTitle,
    // $FlowFixMe
    insideFacilities: mapFacilities(facilities, productFeaturesLineData, true),
    // $FlowFixMe
    outsideFacilities: mapFacilities(facilities, productFeaturesLineData, false),
    facilitiesTitle,
    insideFacilitiesTitle: resortFacilitiesTitle,
    outsideFacilitiesTitle,
    targetPrice: {
      ...get(childcare, 'targetPrice', {}),
      label: productFeaturesLineData?.config.common.price?.priceFrom || '',
    },
    generalInformation: mapGeneralInformation(activityId, productFeaturesLineData, activities),
    generalInformationTitle,
    generalInformationExtraCost: productFeaturesLineData.config.common.extraCost,
    title,
    titleColor: color,
  };
}

function resolveActivityServices({
  activityId,
  color,
  description,
  hasBanner,
  icon,
  image,
  isIncluded,
  title,
  notIncludedMessage,
  productFeaturesLineData,
  activities,
  philosophy,
  brandLogoUrl,
}: {
  activityId: string,
  color: ?string,
  description: ?string,
  hasBanner: boolean,
  icon: ?string,
  image: string,
  isIncluded: boolean,
  title: string,
  notIncludedMessage?: string,
  productFeaturesLineData: ProductFeaturesLineProps,
  activities: Array<ProductFeaturesActivityData>,
  philosophy: ?string,
  brandLogoUrl: ?string,
}) {
  return ({ productActivities }: { productActivities: ProductFeatureDetailData }) =>
    mapActivityData({
      activityId,
      color,
      description,
      hasBanner,
      icon,
      image,
      isIncluded,
      title,
      activityDetail: productActivities,
      notIncludedMessage,
      productFeaturesLineData,
      activities,
      philosophy,
      brandLogoUrl,
    });
}

function getActivityKeyword(keywords: $ReadOnlyArray<string> = []) {
  const keyword = keywords.find(k => featuresIcons.hasOwnProperty(k));
  return keyword
    ? featuresIcons[keyword]
    : {
        icon: ICONS.TRIDENT1,
        color: undefined,
      };
}

export function mapActivitiesPerCategory({
  activities = [],
  categoryId = '',
  fetchActivityServices,
  hasBanner = false,
  notIncludedMessage,
  productFeaturesLineData,
  labelGAPrefix,
}: {
  activities: Array<ProductFeaturesActivityData>,
  categoryId: string,
  fetchActivityServices?: ProductFeatureFetchDetailFunction,
  hasBanner?: boolean,
  notIncludedMessage?: string,
  productFeaturesLineData: ProductFeaturesLineProps,
  labelGAPrefix: string,
}): Array<ProductFeatureCardProps> {
  const { config, includedTitle, onDemandTitle, resortId, seeMore } = productFeaturesLineData;

  return activities
    .sort(sortActivities)
    .map<ProductFeatureCardProps | null>(
      ({
        id: activityId,
        isChildrenClub,
        isIncluded,
        label,
        name,
        description,
        keywords,
        image,
        numberOfIncludedActivities,
        numberOfOnDemandActivities,
        philosophy,
        brandLogoUrl,
      }) => {
        if (!image) {
          image = 'invalid';
        }
        if (!activityId || !name || !label) {
          return null;
        }
        const { icon, color } = getActivityKeyword(keywords || []);

        const hasOnDemandBanner = hasBanner && !isIncluded;
        const hasIncludedBanner = hasBanner && isChildrenClub && isIncluded;

        // $FlowFixMe
        return {
          ...(hasOnDemandBanner || hasIncludedBanner
            ? {
                banner: {
                  color: isIncluded ? COLORS.BLUE : COLORS.AMBER,
                  iconName: isIncluded ? ICONS.ALL_INCLUSIVE : ICONS.ON_DEMAND,
                  label: isIncluded ? config.common.included : config.common.childcarealacarte,
                },
              }
            : {}),
          counters: [
            {
              color: COLORS.BLUE,
              count: numberOfIncludedActivities || 0,
              icon: ICONS.ALL_INCLUSIVE,
              label: includedTitle,
            },
            {
              color: COLORS.AMBER,
              count: numberOfOnDemandActivities || 0,
              icon: ICONS.ON_DEMAND,
              label: onDemandTitle,
            },
          ],
          fetchDetail: () =>
            fetchActivityServices &&
            fetchActivityServices({ productId: resortId, activityId }).then(
              resolveActivityServices({
                activityId,
                color,
                description,
                hasBanner,
                icon,
                image,
                isIncluded,
                notIncludedMessage,
                philosophy,
                brandLogoUrl,
                title: label,
                productFeaturesLineData,
                activities,
              }),
            ),
          imageSrc: image,
          trackingData: {
            actionGA: 'blockInfos',
            labelGA: `${labelGAPrefix}_${categoryId}_${name}`,
          },
          seeMoreLabel: seeMore,
          title: label,
          titleColor: color ? color : COLORS.GRAY_DARKER,
        };
      },
    )
    .filter(Boolean);
}

export function mapCategories({
  fetchActivityServices,
  hasBanner,
  labelGAPrefix,
  notIncludedMessage,
  productActivitiesCategories,
  productFeaturesLineData,
}: {
  fetchActivityServices?: ProductFeatureFetchDetailFunction,
  hasBanner?: boolean,
  labelGAPrefix: string,
  notIncludedMessage?: string,
  productActivitiesCategories: Array<ProductFeaturesCategoryData>,
  productFeaturesLineData: ProductFeaturesLineProps,
}) {
  return productActivitiesCategories
    .sort(sortActivities)
    .map<ProductFeaturesCategoryProps | null>(({ id, activities, label } = {}) => {
      if (!id || !label) {
        return null;
      }
      const activitiesItems = mapActivitiesPerCategory({
        activities,
        categoryId: id,
        fetchActivityServices,
        hasBanner,
        labelGAPrefix,
        notIncludedMessage,
        productActivitiesCategories,
        productFeaturesLineData,
      });
      return {
        backNavigationLabel: productFeaturesLineData.config.common.backNavigation,
        errorMessage: productFeaturesLineData.noDetailsErrorMessage,
        id,
        title: label,
        activities: activitiesItems,
      };
    })
    .filter(Boolean);
}
